import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Typography, Button, Checkbox, Input } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { BusinessState } from "redux-context/business";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import type {
  SubscriptionCancellationAction,
  SubscriptionCancellationPayload,
  SubscriptionCancellationReasonCategory,
} from "interfaces/subscription-cancellation";
import "./CancelSubscriptionModal.scss";

const CANCELLATION_REASON_OPTIONS: {
  label: string;
  value: SubscriptionCancellationReasonCategory;
}[] = [
  {
    label: "Not enough relevant creators in my area",
    value: "not_enough_creators",
  },
  {
    label: "Creators weren't a good fit for my brand",
    value: "creators_not_fit",
  },
  {
    label: "Campaign performance didn't meet expectations",
    value: "campaign_performance",
  },
  {
    label: "Pricing didn't align with my budget",
    value: "pricing",
  },
  {
    label: "Haven't had time to use the platform",
    value: "no_time",
  },
  {
    label: "Platform was difficult to use",
    value: "difficult_to_use",
  },
  {
    label: "Prefer to manage outreach in-house",
    value: "manage_in_house",
  },
  {
    label: "Other (please specify)",
    value: "other",
  },
];

const OTHER_REASON_MAX_LENGTH = 500;

export type CancelSubscriptionModalInteraction = {
  reasonCategory: SubscriptionCancellationReasonCategory | null;
  reasonText: string;
  cancelInfluencerBookings: boolean;
};

interface CancelSubscriptionModalProps {
  visible: boolean;
  loadingAction?: SubscriptionCancellationAction | null;
  onCancel: () => void;
  onConfirm: (payload: SubscriptionCancellationPayload) => void;
  onPause: (payload: SubscriptionCancellationPayload) => void;
  onReasonSelected?: (payload: CancelSubscriptionModalInteraction) => void;
  onAbandon?: (payload: CancelSubscriptionModalInteraction) => void;
}

const CancelSubscriptionModal: React.FC<CancelSubscriptionModalProps> = ({
  visible,
  loadingAction = null,
  onCancel,
  onConfirm,
  onPause,
  onReasonSelected,
  onAbandon,
}) => {
  const [cancelInfluencerBookings, setCancelInfluencerBookings] =
    useState<boolean>(true);
  const [selectedReason, setSelectedReason] =
    useState<SubscriptionCancellationReasonCategory | null>(null);
  const [otherReasonText, setOtherReasonText] = useState<string>("");
  const [otherReasonTouched, setOtherReasonTouched] = useState<boolean>(false);
  const hasInteractedRef = useRef<boolean>(false);
  const hasSubmittedRef = useRef<boolean>(false);

  const { subscription, business } = useSelector(
    (state: BusinessState) => state.business,
  );

  const nextPaymentDate = subscription?.stripeDetails?.nextPaymentDetails
    ? dayjs(subscription?.stripeDetails?.nextPaymentDetails?.date).format(
        "MMM DD, YYYY",
      )
    : "";

  const trimmedOtherReasonText = useMemo(() => {
    return otherReasonText.trim();
  }, [otherReasonText]);

  const isOtherReasonSelected = selectedReason === "other";

  const isOtherReasonMissing =
    isOtherReasonSelected && trimmedOtherReasonText.length === 0;

  const isOtherReasonTooLong =
    isOtherReasonSelected &&
    trimmedOtherReasonText.length > OTHER_REASON_MAX_LENGTH;

  const isFeedbackValid =
    Boolean(selectedReason) && !isOtherReasonMissing && !isOtherReasonTooLong;

  const canSubmit =
    Boolean(business?._id) &&
    isFeedbackValid &&
    loadingAction !== "cancel" &&
    loadingAction !== "pause";

  const buildInteractionPayload = (): CancelSubscriptionModalInteraction => ({
    reasonCategory: selectedReason,
    reasonText: isOtherReasonSelected ? trimmedOtherReasonText : "",
    cancelInfluencerBookings,
  });

  const buildRequestPayload = (): SubscriptionCancellationPayload | null => {
    if (!business?._id || !selectedReason || !isFeedbackValid) {
      return null;
    }

    return {
      businessId: business._id,
      cancelInfluencerBookings,
      reasonCategory: selectedReason,
      reasonText: isOtherReasonSelected ? trimmedOtherReasonText : undefined,
    };
  };

  const closeModal = () => {
    if (hasInteractedRef.current && !hasSubmittedRef.current) {
      onAbandon?.(buildInteractionPayload());
    }

    onCancel();
  };

  const onSelectReason = (value: SubscriptionCancellationReasonCategory) => {
    hasInteractedRef.current = true;
    setSelectedReason(value);

    if (value !== "other") {
      setOtherReasonTouched(false);
    }

    onReasonSelected?.({
      reasonCategory: value,
      reasonText: value === "other" ? trimmedOtherReasonText : "",
      cancelInfluencerBookings,
    });
  };

  const onChangeOtherReason = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    hasInteractedRef.current = true;
    setOtherReasonTouched(true);
    setOtherReasonText(event.target.value);
  };

  const onToggleCancelInfluencerBookings = (checked: boolean) => {
    hasInteractedRef.current = true;
    setCancelInfluencerBookings(checked);
  };

  const onConfirmCancellation = () => {
    const payload = buildRequestPayload();
    if (!payload) {
      return;
    }

    hasSubmittedRef.current = true;
    onConfirm(payload);
  };

  const onRequestPause = () => {
    const payload = buildRequestPayload();
    if (!payload) {
      return;
    }

    hasSubmittedRef.current = true;
    onPause(payload);
  };

  useEffect(() => {
    if (!visible) {
      return;
    }

    setCancelInfluencerBookings(true);
    setSelectedReason(null);
    setOtherReasonText("");
    setOtherReasonTouched(false);
    hasInteractedRef.current = false;
    hasSubmittedRef.current = false;
  }, [visible]);

  return (
    <Modal
      open={visible}
      onCancel={closeModal}
      footer={null}
      centered
      width={640}
      className="cancel-subscription-modal"
      rootClassName="cancel-subscription-modal-root"
    >
      <div className="cancel-subscription-modal__layout">
        <div className="cancel-subscription-modal__header">
          <button
            type="button"
            className="cancel-subscription-modal__close"
            onClick={closeModal}
            aria-label="Close cancel subscription modal"
            disabled={loadingAction === "cancel" || loadingAction === "pause"}
          >
            x
          </button>

          <div className="cancel-subscription-modal__icon-wrap" aria-hidden>
            <FontAwesomeIcon icon={faTriangleExclamation} color="#FF4D6D" />
          </div>

          <Typography.Title level={3}>Cancel Subscription</Typography.Title>

          <Typography.Paragraph>
            Are you sure you want to cancel your subscription? You'll lose
            access to the Mustard Platform at the end of your billing cycle.
          </Typography.Paragraph>
        </div>

        <div className="cancel-subscription-modal__body">
          <div className="cancel-subscription-modal__summary-card">
            <div>
              <span>Current Plan</span>
              <strong>{subscription?.planName || "N/A"}</strong>
            </div>
            <div>
              <span>Access Until</span>
              <strong>{nextPaymentDate || "N/A"}</strong>
            </div>
          </div>

          <div className="cancel-subscription-modal__reasons-block">
            <Typography.Title level={5}>
              Before you go, what didn't work for you?
            </Typography.Title>

            <div className="cancel-subscription-modal__reasons-grid">
              {CANCELLATION_REASON_OPTIONS.map((option) => {
                const isSelected = selectedReason === option.value;

                return (
                  <button
                    type="button"
                    key={option.value}
                    className={`cancel-subscription-modal__reason-card ${
                      isSelected ? "selected" : ""
                    }`.trim()}
                    onClick={() => onSelectReason(option.value)}
                    aria-pressed={isSelected}
                  >
                    <span
                      className={`cancel-subscription-modal__reason-radio ${
                        isSelected ? "selected" : ""
                      }`.trim()}
                      aria-hidden
                    >
                      <span className="cancel-subscription-modal__reason-dot" />
                    </span>
                    <span>{option.label}</span>
                  </button>
                );
              })}
            </div>

            {isOtherReasonSelected ? (
              <div className="cancel-subscription-modal__other-input-wrap">
                <Input.TextArea
                  rows={4}
                  value={otherReasonText}
                  onChange={onChangeOtherReason}
                  placeholder="Tell us more about your experience..."
                  maxLength={OTHER_REASON_MAX_LENGTH + 1}
                />
                <div className="cancel-subscription-modal__other-meta">
                  <span>
                    {isOtherReasonTooLong
                      ? `Please keep it under ${OTHER_REASON_MAX_LENGTH} characters.`
                      : " "}
                  </span>
                  <span>
                    {Math.min(
                      trimmedOtherReasonText.length,
                      OTHER_REASON_MAX_LENGTH,
                    )}
                    /{OTHER_REASON_MAX_LENGTH}
                  </span>
                </div>
                {otherReasonTouched && isOtherReasonMissing ? (
                  <Typography.Text type="danger">
                    Please provide details for "Other".
                  </Typography.Text>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="cancel-subscription-modal__bookings-card">
            <Checkbox
              checked={cancelInfluencerBookings}
              onChange={(event) =>
                onToggleCancelInfluencerBookings(event.target.checked)
              }
            >
              Cancel all upcoming influencer bookings
            </Checkbox>

            <Typography.Paragraph>
              This will automatically cancel any pending or scheduled
              collaborations with creators.
            </Typography.Paragraph>

            <a
              href="https://www.mustard.love/terms-of-service#terms-of-payment"
              target="_blank"
              rel="noopener noreferrer"
            >
              Refund And Cancellation Policy
            </a>
          </div>
        </div>

        <div className="cancel-subscription-modal__footer">
          <Button
            type="primary"
            danger
            block
            onClick={onConfirmCancellation}
            disabled={!canSubmit || loadingAction === "pause"}
            loading={loadingAction === "cancel"}
          >
            Yes, Cancel Subscription
          </Button>

          <div className="cancel-subscription-modal__footer-actions">
            <Button
              block
              onClick={onRequestPause}
              disabled={!canSubmit || loadingAction === "cancel"}
              loading={loadingAction === "pause"}
              style={{ display: "none" }}
            >
              Pause 1 Month
            </Button>
            <Button
              block
              onClick={closeModal}
              disabled={loadingAction === "cancel" || loadingAction === "pause"}
            >
              Keep Subscription
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CancelSubscriptionModal;
